type IconFamily = "stat" | "item"
type IconTier = "common" | "uncommon" | "rare" | "legendary"

interface IconPalette {
  frame: string
  bg: string
  bg2: string
  accent: string
  accent2: string
  ink: string
}

export interface IconAssetDefinition {
  id: string
  label: string
  assetPath: string
  textureKey: string
  family: IconFamily
  sourceType: "upgrade"
  sourceId: string
  motif: string
  description: string
  tier?: IconTier
  palette: IconPalette
}

function makeUpgradeIcon(
  sourceId: string,
  label: string,
  assetPath: string,
  textureKey: string,
  motif: string,
  description: string,
  palette: IconPalette,
  tier: IconTier,
  family: IconFamily,
): IconAssetDefinition {
  return {
    id: `icon:upgrade:${sourceId}`,
    label,
    assetPath,
    textureKey,
    family,
    sourceType: "upgrade",
    sourceId,
    motif,
    description,
    tier,
    palette,
  }
}

const P = {
  hp: {
    frame: "#fb7185",
    bg: "#2a0d18",
    bg2: "#17080d",
    accent: "#fda4af",
    accent2: "#fecdd3",
    ink: "#fff1f2",
  },
  speed: {
    frame: "#38bdf8",
    bg: "#0a1a2a",
    bg2: "#07111a",
    accent: "#7dd3fc",
    accent2: "#bae6fd",
    ink: "#f0f9ff",
  },
  tech: {
    frame: "#22d3ee",
    bg: "#0a202a",
    bg2: "#06141a",
    accent: "#67e8f9",
    accent2: "#a5f3fc",
    ink: "#ecfeff",
  },
  damage: {
    frame: "#f97316",
    bg: "#2a1208",
    bg2: "#180a04",
    accent: "#fb923c",
    accent2: "#fdba74",
    ink: "#fff7ed",
  },
  defense: {
    frame: "#60a5fa",
    bg: "#0d1729",
    bg2: "#08101a",
    accent: "#93c5fd",
    accent2: "#dbeafe",
    ink: "#eff6ff",
  },
} as const

export const UPGRADE_ICON_DEFINITIONS: IconAssetDefinition[] = [
  makeUpgradeIcon(
    "hp-boost",
    "HP Boost",
    "icons/upgrades/hp-boost.webp",
    "icon_upgrade_hp_boost",
    "heart-boost",
    "Max HP upgrade card.",
    P.hp,
    "common",
    "stat",
  ),
  makeUpgradeIcon(
    "attack-speed",
    "Attack Speed",
    "icons/upgrades/attack-speed.webp",
    "icon_upgrade_attack_speed",
    "speed-bolt",
    "Ball speed upgrade card.",
    P.speed,
    "uncommon",
    "stat",
  ),
  makeUpgradeIcon(
    "ball-bounces",
    "Bounce",
    "icons/upgrades/pinball-router.webp",
    "icon_upgrade_ball_bounces",
    "router",
    "Extra ball bounce card.",
    P.tech,
    "common",
    "stat",
  ),
  makeUpgradeIcon(
    "potion",
    "Kibble",
    "icons/upgrades/potion.webp",
    "icon_item_potion",
    "potion",
    "Small handful of crunchy kibble.",
    P.hp,
    "common",
    "item",
  ),
  makeUpgradeIcon(
    "hi-potion",
    "Chicken Leg",
    "icons/upgrades/hi-potion.webp",
    "icon_item_hi_potion",
    "hi-potion",
    "Juicy bone, real sustain.",
    P.hp,
    "uncommon",
    "item",
  ),
  makeUpgradeIcon(
    "pinball-router",
    "Pinball Router",
    "icons/upgrades/pinball-router.webp",
    "icon_item_pinball_router",
    "router",
    "Bumper-routing network icon.",
    P.tech,
    "common",
    "item",
  ),
  makeUpgradeIcon(
    "hot-route",
    "Hot Route",
    "icons/upgrades/hot-route.webp",
    "icon_item_hot_route",
    "flame-route",
    "Fire-route bumper icon.",
    P.damage,
    "uncommon",
    "item",
  ),
  makeUpgradeIcon(
    "crash-helmet",
    "Crash Helmet",
    "icons/upgrades/crash-helmet.webp",
    "icon_item_crash_helmet",
    "helmet",
    "Wave guard icon.",
    P.defense,
    "rare",
    "item",
  ),
]

export const ICON_ASSET_DEFINITIONS: IconAssetDefinition[] = [
  ...UPGRADE_ICON_DEFINITIONS,
]

export const ICON_ASSET_BY_TEXTURE_KEY: Record<string, IconAssetDefinition> =
  Object.fromEntries(
    ICON_ASSET_DEFINITIONS.map((icon) => [icon.textureKey, icon]),
  )

export function getIconAssetById(id: string): IconAssetDefinition | undefined {
  return ICON_ASSET_DEFINITIONS.find((icon) => icon.id === id)
}

export const UPGRADE_ICON_TEXTURE_KEYS = Object.fromEntries(
  UPGRADE_ICON_DEFINITIONS.map((icon) => [icon.sourceId, icon.textureKey]),
) as Record<string, string>
